import React, { useMemo } from "react";

const AttendancePercentage = ({ attendancePercentage }) => {
  // Convert attendance percentage to a valid number
  const percentage = parseFloat(attendancePercentage);

  // Ensure percentage is within valid bounds
  const validPercentage = Math.min(100, Math.max(0, percentage));

  // Format percentage to 2 decimal places
  const formattedPercentage = validPercentage.toFixed(2);


  // Determine stroke color dynamically
  const strokeColor = useMemo(() => {
    if (validPercentage < 40) return "#EF4444"; // Red (Low Attendance)
    if (validPercentage < 70) return "#F59E0B"; // Orange (Moderate Attendance)
    return "#10B981"; // Green (High Attendance)
  }, [validPercentage]);

  // Calculate strokeDashoffset dynamically
  const strokeDashoffset = 314 - (validPercentage * 314) / 100;

  return (
    <div className="panel bg-white shadow-2xl rounded-xl p-6 flex flex-col items-center transition-transform h-64 duration-300 hover:scale-105 hover:shadow-3xl">
      {/* Top gradient bar */}
  <div className="absolute top-0 left-0 w-full h-[10px] bg-gradient-to-r from-[#EE1D23] via-pink-500 to-[#1A2B5C] rounded-t-[2rem] z-0" />
      {/* Unique Attendance Title */}
      <h5 className="text-xl font-bold text-gray-800 mb-2">Attendance Overview</h5>

      {/* Circular Progress Bar */}
      <div className="relative w-32 h-32">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
          {/* Background Circle */}
          <circle
            className="text-gray-300 stroke-current"
            strokeWidth="10"
            cx="60"
            cy="60"
            r="50"
            fill="transparent"
          />
          {/* Progress Circle with Dynamic Color */}
          <circle
            className="transition-all duration-500 ease-out drop-shadow-lg"
            stroke={strokeColor}
            strokeWidth="10"
            strokeDasharray="314"
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            cx="60"
            cy="60"
            r="50"
            fill="transparent"
          />
        </svg>
        {/* Percentage in Center */}
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-3xl font-extrabold text-black">{formattedPercentage}%</p>
        </div>
      </div>

      {/* Unique Attendance Legend */}
      <div className="mt-4 w-full flex flex-col items-center text-gray-700">
        <p className="text-sm font-semibold mb-2">Attendance Categories:</p>
        <div className="grid grid-cols-3 gap-3 w-full px-3">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500 shadow-md"></div>
            <p className="text-xs">Low &lt; 40%</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500 shadow-md"></div>
            <p className="text-xs">Average 40-70%</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-green-500 shadow-md"></div>
            <p className="text-xs">Good &gt; 70%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendancePercentage;
